import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import {
  SelfOnboardingStepKey,
  Jobsite,
  SelfOnboardingNavigation,
  SelfOnboardingLocationState,
  SelfOnboardingState,
  SelfOnboardingLocationSearch,
  GoToStepOptions,
  SelfOnboardingNavigationStepKey,
} from 'containers/selfOnboarding/types';
import {
  defaultStep,
  getBaseRoute,
  resolveSelfOnboardingStep,
} from 'containers/selfOnboarding/utils';
import { DocumentKey } from 'containers/worker/utils';
import { isStringEnumValue } from 'utils';
import {
  useCompleteSelfOnBoardingMutation,
  useUpdateWorkerMutation,
} from 'apollo/generated/client-operations';
import { useFlags } from 'featureFlags';
import { getVisibleSequentialSteps } from './utils';
import {
  lastStep,
  getNoGoBackSteps,
  noOrderInfoSteps,
  getWhiteListedSteps,
  previousSteps,
} from './useSelfOnboardingNavigation.config';

type UseSelfOnboardingNavigationArgs = {
  selfOnboardingState: SelfOnboardingState;
  jobsite: Jobsite;
};

export function useSelfOnboardingNavigation(
  args: UseSelfOnboardingNavigationArgs,
): SelfOnboardingNavigation {
  const { selfOnboardingState, jobsite } = args;
  const { authenticatedSelfOnboardingEnabled } = useFlags();
  const { worker, jobsiteWorker, contractorId, shouldSelectContractor } =
    selfOnboardingState ?? {};
  const { workerId, selfOnboardingCurrentStep, isAssignedToMultipleJobsites } =
    worker ?? {};
  const { jobsiteWorkerId } = jobsiteWorker ?? {};
  const isSelfOnboardingClosedOrCompleted =
    selfOnboardingCurrentStep === SelfOnboardingStepKey.Closed ||
    selfOnboardingCurrentStep === lastStep;
  const history = useHistory();
  const location = useLocation<SelfOnboardingLocationState>();
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [isReview, setIsReview] = React.useState(false);
  const [updateWorker] = useUpdateWorkerMutation();
  const [completeSelfOnBoarding] = useCompleteSelfOnBoardingMutation();

  const locationState = location.state ?? {};
  const { jobsiteInvitationId, step: urlStep } = useParams<{
    jobsiteInvitationId: string;
    step: string;
  }>();
  const baseRoute = getBaseRoute(jobsiteInvitationId);

  const locationSearchRef = React.useRef(location.search);
  const updateQueryParams = (
    queryParams?: SelfOnboardingLocationSearch,
  ): void => {
    locationSearchRef.current = `?${queryString.stringify(queryParams, {
      skipEmptyString: true,
      skipNull: true,
    })}`;
  };

  const confirmInitialization = (): void => {
    if (!isInitialized) {
      setIsInitialized(true);
    }
  };

  const goToStep = React.useCallback<SelfOnboardingNavigation['goToStep']>(
    (step: SelfOnboardingNavigationStepKey, options?: GoToStepOptions) => {
      const { tab, state, shouldReplace } = options ?? {};
      const pathname = [baseRoute, step === defaultStep ? '' : step, tab]
        .filter(Boolean)
        .join('/');
      const goTo = shouldReplace ? history.replace : history.push;
      goTo({ pathname, state, search: locationSearchRef.current });

      if (step === lastStep && !isReview) {
        setIsReview(true);
      }
    },
    [history, baseRoute, isReview],
  );

  const { visibleSequentialSteps, visibleSequentialStepKeys } =
    React.useMemo(() => {
      const vss = getVisibleSequentialSteps({
        ...selfOnboardingState,
        jobsite,
        authenticatedSelfOnboardingEnabled,
      });
      return {
        visibleSequentialSteps: vss,
        visibleSequentialStepKeys: vss.map((s) =>
          typeof s === 'string' ? s : s.key,
        ),
      };
    }, [
      jobsite,
      isAssignedToMultipleJobsites,
      shouldSelectContractor,
      authenticatedSelfOnboardingEnabled,
    ]);

  const getStepIndex = React.useCallback<
    SelfOnboardingNavigation['getStepIndex']
  >(
    (step) => visibleSequentialStepKeys.findIndex((s) => s === step),
    [visibleSequentialStepKeys],
  );

  const selfOnboardingStepInfo = resolveSelfOnboardingStep(
    urlStep,
    visibleSequentialSteps,
  );
  const { currentStep, currentConfigStep } = selfOnboardingStepInfo;

  const shouldGoToContractorSelectionStep = Boolean(
    // Legacy flow only: contractor selection is the entry step and is
    // force-redirected to. The authenticated flow orders contractor selection
    // AFTER OTP and reaches it explicitly, so this early redirect must not fire
    // there — otherwise the worker is directed to it pre-OTP, then bounced back
    // to language by the phone-verified whitelist gate.
    !authenticatedSelfOnboardingEnabled &&
      shouldSelectContractor &&
      !contractorId &&
      (currentStep !== SelfOnboardingStepKey.ContractorSelection ||
        locationSearchRef.current),
  );
  const workerWillBeAssigned = !shouldSelectContractor && !jobsiteWorker;

  // This effect handles the situations when `selfOnboardingCurrentStep` exists and
  // the user has to be redirected either to `selfOnboardingCurrentStep` or `defaultStep`
  React.useEffect(() => {
    if (selfOnboardingCurrentStep === SelfOnboardingStepKey.Closed) {
      if (currentStep !== SelfOnboardingStepKey.Closed) {
        goToStep(SelfOnboardingStepKey.Closed);
      }
      confirmInitialization();
      return;
    }

    // if `shouldGoToContractorSelectionStep` is true or if the worker is to be assigned to jobsite-contractor
    // then complete the initialization and defer the redirection to the next effect
    if (shouldGoToContractorSelectionStep || workerWillBeAssigned) {
      confirmInitialization();
    } else if (
      jobsiteWorker &&
      isStringEnumValue(SelfOnboardingStepKey, selfOnboardingCurrentStep)
    ) {
      const currentStepIndex = getStepIndex(currentStep);
      const selfOnboardingCurrentStepIndex = getStepIndex(
        selfOnboardingCurrentStep,
      );

      const canGoToPersistedCurrentStep =
        selfOnboardingCurrentStepIndex >= 0 ||
        isSelfOnboardingClosedOrCompleted;
      if (canGoToPersistedCurrentStep) {
        const shouldGoToPersistedCurrentStep =
          currentStep === defaultStep ||
          isSelfOnboardingClosedOrCompleted ||
          currentStepIndex === -1 ||
          selfOnboardingCurrentStepIndex < currentStepIndex;

        if (shouldGoToPersistedCurrentStep) {
          goToStep(selfOnboardingCurrentStep);
        }
        confirmInitialization();
      } else if (currentStep !== defaultStep) {
        goToStep(defaultStep);
        confirmInitialization();
      }
    }
  }, [selfOnboardingCurrentStep]);

  const isCurrentStepWhiteListed = (): boolean => {
    return getWhiteListedSteps(authenticatedSelfOnboardingEnabled).some(
      (step) => {
        return typeof step === 'string'
          ? step === currentStep
          : step.key === currentStep && step.isWhiteListed(selfOnboardingState);
      },
    );
  };

  // This effect handles the situations when the user has to be redirected to ContractorSelection step or `defaultStep`
  React.useEffect(() => {
    if (currentStep !== defaultStep || selfOnboardingCurrentStep) {
      if (shouldGoToContractorSelectionStep) {
        updateQueryParams();
        goToStep(SelfOnboardingStepKey.ContractorSelection);
      }
      // if current step is not defaultStep and there is no current worker then go to defaultStep
      else if (
        worker === null && // session query completed with no current worker
        !isCurrentStepWhiteListed()
      ) {
        goToStep(defaultStep);
      }
    }
    if (
      worker !== undefined && // session query completed
      (!isStringEnumValue(SelfOnboardingStepKey, selfOnboardingCurrentStep) ||
        currentStep === selfOnboardingCurrentStep)
    ) {
      confirmInitialization();
    }
  }, [currentStep, worker]);

  const goToLastStep = React.useCallback<
    SelfOnboardingNavigation['goToLastStep']
  >(
    async (tab) => {
      goToStep(lastStep, { tab });
      if (workerId) {
        await updateWorker({
          variables: {
            workerId,
            workerInput: { selfOnboardingCurrentStep: lastStep },
          },
        });
        await completeSelfOnBoarding({ variables: { jobsiteWorkerId } });
      }
    },
    [goToStep, workerId, jobsiteWorkerId],
  );

  const getNextStep = React.useCallback<
    SelfOnboardingNavigation['getNextStep']
  >(() => {
    const currentStepIndex = getStepIndex(currentStep);
    return visibleSequentialStepKeys[currentStepIndex + 1];
  }, [visibleSequentialStepKeys, currentStep, getStepIndex]);

  const goToNextStep = React.useCallback<
    SelfOnboardingNavigation['goToNextStep']
  >(async () => {
    const currentStepIndex = getStepIndex(currentStep);
    const nextStep = isReview
      ? null
      : visibleSequentialStepKeys[currentStepIndex + 1];

    goToStep(
      nextStep ?? lastStep,
      isReview && isStringEnumValue(DocumentKey, currentConfigStep)
        ? { tab: 'documents' }
        : null,
    );

    if (workerId && nextStep) {
      await updateWorker({
        variables: {
          workerId,
          workerInput: { selfOnboardingCurrentStep: nextStep },
        },
      });
      if (nextStep === lastStep) {
        await completeSelfOnBoarding({ variables: { jobsiteWorkerId } });
      }
    }
  }, [
    workerId,
    jobsiteWorkerId,
    isReview,
    currentStep,
    getStepIndex,
    goToStep,
    visibleSequentialStepKeys,
  ]);

  const goToPreviousStep = React.useCallback(() => {
    const currentStepIndex = getStepIndex(currentStep);
    const firstStep = shouldSelectContractor
      ? SelfOnboardingStepKey.ContractorSelection
      : defaultStep;
    const prevStep =
      previousSteps[currentStep as SelfOnboardingStepKey] ??
      visibleSequentialStepKeys[currentStepIndex - 1] ??
      firstStep;
    goToStep(prevStep);
  }, [currentStep, getStepIndex, goToStep, visibleSequentialStepKeys]);

  const getStepNumberInfo = (): { stepsCount: number; currentStep: number } => {
    const orderedSteps = visibleSequentialStepKeys.filter(
      (step) => !noOrderInfoSteps.includes(step as SelfOnboardingStepKey),
    );
    const currentStepIndex = orderedSteps.findIndex(
      (step) => step === currentStep,
    );
    if (currentStepIndex >= 0) {
      return {
        stepsCount: orderedSteps.length,
        currentStep: currentStepIndex + 1,
      };
    }
    return null;
  };

  const noGoBackSteps = React.useMemo(() => {
    return getNoGoBackSteps({
      ...selfOnboardingState,
      jobsite: null,
      authenticatedSelfOnboardingEnabled,
    });
  }, [shouldSelectContractor, authenticatedSelfOnboardingEnabled]);

  const canGoToPreviousStep: SelfOnboardingNavigation['canGoToPreviousStep'] =
    React.useCallback(
      (step) => !noGoBackSteps.includes(step as SelfOnboardingStepKey),
      [noGoBackSteps],
    );

  return React.useMemo<SelfOnboardingNavigation>(
    () => ({
      loading: !isInitialized,
      currentStep,
      currentConfigStep,
      state: { ...locationState, isReview },
      goToStep,
      goToNextStep,
      canGoToPreviousStep,
      goToPreviousStep,
      goToLastStep,
      getStepNumberInfo,
      getStepIndex,
      getNextStep,
      updateQueryParams,
    }),
    [
      isInitialized,
      currentStep,
      currentConfigStep,
      locationState,
      isReview,
      goToStep,
      goToNextStep,
      canGoToPreviousStep,
      goToPreviousStep,
      goToLastStep,
      getStepNumberInfo,
      getStepIndex,
      getNextStep,
    ],
  );
}
